import { useLayoutEffect, useState, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import Dashboard from "../../layouts/Dashboard";
import styles from "./Apps.module.scss";

// Props interface for the PushPressApp component
// src: The URL to load in the iframe
// title: The title attribute for the iframe (for accessibility)
interface PushPressAppProps {
  src: string;
  title: string;
}

const PushPressApp = ({ src, title }: PushPressAppProps) => {
  // Reference to the iframe element for postMessage communication
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Get the access token from cookies on component mount
  const [accessToken] = useState(() => Cookies.get("ACCESS_TOKEN"));

  // add token query param to src with the access token, this is for backwards compat
  // with apps that don't use the new postMessage protocol.
  const url = new URL(src);
  url.searchParams.append("token", accessToken);

  // Prevent body scrolling while the iframe is mounted
  useRemoveBodyScroll();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !accessToken) return;

    const handleMessage = (event: MessageEvent) => {
      const targetOrigin = new URL(iframe.src).origin;
      console.debug(
        `Received message from origin ${event.origin}: ${event.data?.type}`,
      );
      if (
        event.origin === targetOrigin &&
        event.data?.type === "READY_FOR_INIT"
      ) {
        console.debug("Message is from expected origin, sending APP_INIT");
        iframe.contentWindow?.postMessage(
          {
            type: "APP_INIT",
            payload: { token: accessToken },
          },
          targetOrigin,
        );
        console.debug(`APP_INIT sent to ${targetOrigin}`);
      }
    };

    window.addEventListener("message", handleMessage);
    console.debug("Waiting for READY_FOR_INIT message from embedded iframe");

    return () => window.removeEventListener("message", handleMessage);
  }, [accessToken]);

  return (
    <Dashboard>
      <div className={styles.root}>
        {/* Iframe element that fills its container */}
        <iframe
          ref={iframeRef}
          title={title}
          className={styles.frame}
          src={url.toString()}
        />
      </div>
    </Dashboard>
  );
};

// Hook to prevent body scrolling while the iframe is mounted
// This ensures the iframe takes up the full viewport without
// allowing the parent page to scroll
const useRemoveBodyScroll = () => {
  useLayoutEffect(() => {
    // Store the original overflow value
    const originalOverflow = document.body.style.overflow;

    // Prevent scrolling
    document.body.style.overflow = "hidden";

    // Restore original overflow value on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);
};

export default PushPressApp;

export type { PushPressAppProps };
